import { Module } from 'vuex';
import type { RootState } from '@/interfaces/store';
import { NavItem } from '@/interfaces/navigation/NavItem.types';
import type { RouteRecordRaw } from 'vue-router';
import services from '@/routes/services';
import laboratory from '@/routes/laboratory';
import crm from '@/routes/crm';
import administration from '@/routes/administration';
import site from '@/routes/site';
import { clientInfo } from '@/routes/client';
import { roles } from '@/constants/roles';
import { crmNavConfig } from '@/crm/src';
export interface NavigationState {
    mainNavItems: NavItem[]; // For top tabs on large screens
    clientSiteNavItems: NavItem[]; // For client/:clientId/site/:siteId
    clientNavItems: NavItem[]; // For client/:clientId
    sidebarBehaviorForLargeScreen: 'expanded' | 'rail';
    activeMainNav: string | null;
}

export const getNavItemMeta = (route: RouteRecordRaw[], parentPath = ''): NavItem[] => {
    return route.map((item) => {
        let fullPath = item.path || '';
        if (!fullPath.startsWith('/') && parentPath) {
            const cleanParent = parentPath.endsWith('/') ? parentPath.slice(0, -1) : parentPath;
            fullPath = cleanParent + '/' + fullPath;
        }

        return {
            name: item.name,
            title: item.meta?.title,
            path: fullPath,
            prependIcon: item.meta?.prependIcon,
            img: item.meta?.img,
            hidden: item.meta?.hidden,
            badge: item.meta?.badge,
            featureFlag: item.meta?.featureFlag,
            hasChildren: item.meta?.hasChildren || (item.children && item.children.length > 0),
            allowedRoles: item.meta?.allowedRoles,
            superAdmin: item.meta?.superAdmin,
            subItems: item.children ? getNavItemMeta(item.children, fullPath) : undefined,
        };
    }) as NavItem[];
};

const defaultMainNavItems: NavItem[] = [
    {
        name: 'dashboard',
        title: 'dashboard.pageName',
        path: '/dashboard',
        prependIcon: 'mdi-chart-box-outline',
        allowedRoles: ['all'],
        featureFlag: 'GLOBAL_DASHBOARD',
    },
    {
        name: 'clients',
        title: 'clients.pageName',
        path: '/clients',
        prependIcon: 'mdi-badge-account-outline',
        allowedRoles: ['all'],
    },
    {
        name: 'sites',
        title: 'sites.pageName',
        path: '/sites',
        prependIcon: 'mdi-office-building-marker-outline',
        allowedRoles: ['all'],
    },
    {
        name: 'services',
        title: 'services.pageName',
        path: '/services',
        prependIcon: 'mdi-hard-hat',
        hasChildren: true,
        allowedRoles: [
            roles.ADMINISTRATOR.value,
            roles.MANAGER.value,
            roles.LABORATORY_TECHNICIAN.value,
            roles.LABORATORY_CHEMIST.value,
            roles.SALES_MANAGER.value,
            roles.SERVICE_TECHNICIAN.value,
            roles.LABORATORY_MANAGER.value,
        ],
        subItems: getNavItemMeta(services.children || [], services.path),
    },
    {
        name: 'laboratory',
        title: 'laboratory.pageName',
        path: '/laboratory',
        prependIcon: 'mdi-flask-outline',
        allowedRoles: [
            roles.ADMINISTRATOR.value,
            roles.MANAGER.value,
            roles.SERVICE_TECHNICIAN.value,
            roles.LABORATORY_MANAGER.value,
            roles.LABORATORY_MICROBIOLOGIST.value,
            roles.LABORATORY_CHEMIST.value,
            roles.LABORATORY_TECHNICIAN.value,
            roles.SALES_MANAGER.value,
            roles.SALES_REPRESENTATIVE.value,
        ],
        subItems: getNavItemMeta(laboratory.children || [], laboratory.path),
    },
    {
        name: crmNavConfig.name,
        title: crmNavConfig.title,
        path: crmNavConfig.path,
        prependIcon: crmNavConfig.prependIcon,
        featureFlag: crmNavConfig.featureFlag,
        allowedRoles: [...crmNavConfig.allowedRoles],
        hasChildren: true,
        subItems: getNavItemMeta(crm.children || [], crm.path),
    },
    {
        name: 'administration',
        title: 'administration.pageName',
        path: '/administration',
        prependIcon: 'mdi-cog-outline',
        allowedRoles: [
            roles.ADMINISTRATOR.value,
            roles.MANAGER.value,
            roles.LABORATORY_MANAGER.value,
            roles.LABORATORY_MICROBIOLOGIST.value,
            roles.FINANCIAL_ADMINISTRATOR.value,
        ],
        subItems: getNavItemMeta(administration.children || [], administration.path),
    },
];

const clientSiteNavItems: NavItem[] = [
    {
        name: 'siteDashboard',
        title: 'site.dashboard',
        path: '/site-dashboard',
        prependIcon: 'mdi-view-dashboard',
        isClientSite: true,
        subItems: getNavItemMeta(site.children || []),
    },
];

const clientNavItems: NavItem[] = [
    {
        name: 'clientDashboard',
        title: 'client.dashboard',
        path: '/client-dashboard',
        prependIcon: 'mdi-view-dashboard',
        isClientSite: true,
        subItems: getNavItemMeta(clientInfo || []),
    },
];

const defaultState: NavigationState = {
    mainNavItems: defaultMainNavItems,
    clientSiteNavItems: clientSiteNavItems,
    clientNavItems: clientNavItems,
    sidebarBehaviorForLargeScreen: 'expanded',
    activeMainNav: null,
};

const navigationModule: Module<NavigationState, RootState> = {
    namespaced: true,
    state: () => defaultState,
    getters: {
        getMainNavItems: (state) => state.mainNavItems,
        getClientSiteNavItems: (state) => state.clientSiteNavItems,
        getClientNavItems: (state) => state.clientNavItems,
        getSidebarBehaviorForLargeScreen: (state) => state.sidebarBehaviorForLargeScreen,
        getActiveMainNav: (state) => state.activeMainNav,
    },
    mutations: {
        SET_MAIN_NAV_ITEMS(state, items: NavItem[]) {
            state.mainNavItems = items;
        },
        SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN(state, behavior: 'expanded' | 'rail') {
            state.sidebarBehaviorForLargeScreen = behavior;
        },
    },
    actions: {
        loadInitialNavigation({ commit }) {
            commit('SET_MAIN_NAV_ITEMS', defaultMainNavItems);
            commit('SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN', 'rail');
        },

        updateSidebarStateForRoute({ commit }, routePath: string) {
            interface RouteConfig {
                items: NavItem[];
                behavior: 'expanded' | 'rail';
            }

            // Special case: only exact /clients route should be rail mode
            if (
                routePath === '/clients' ||
                routePath === '/sites' ||
                routePath === '/teams' ||
                routePath === '/profile'
            ) {
                commit('SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN', 'rail');
                return;
            }

            const DEFAULT_SIDEBAR_CONFIG: RouteConfig = {
                items: defaultMainNavItems,
                behavior: 'expanded',
            };

            let bestMatchPrefix: string | null = null;
            for (const prefix in defaultMainNavItems) {
                if (routePath.startsWith(prefix)) {
                    if (bestMatchPrefix === null || prefix.length > bestMatchPrefix.length) {
                        bestMatchPrefix = prefix;
                    }
                }
            }

            commit('SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN', DEFAULT_SIDEBAR_CONFIG.behavior);
        },

        setSidebarBehaviorForLargeScreen({ commit }, mode: 'expanded' | 'rail') {
            commit('SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN', mode);
        },

        toggleSidebarBehavior({ commit, state }) {
            const newMode =
                state.sidebarBehaviorForLargeScreen === 'expanded' ? 'rail' : 'expanded';
            commit('SET_SIDEBAR_BEHAVIOR_FOR_LARGE_SCREEN', newMode);
        },
    },
};

export default navigationModule;
